import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { Target } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useConversionEventsQuery, useSendConversionEvent } from '@/queries/conversations'
import { conversationsErrorToUserMessage } from '@/services/conversationsApi'

/** Platforms whose chats Meta can attribute a conversion to. */
const CONVERSION_PLATFORMS = ['whatsapp', 'instagram', 'facebook']

const DEFAULT_EVENT = 'QualifiedLead'

/**
 * Report a conversion of this chat (a qualified lead, a sale) to Meta, which
 * credits it to the ad that brought the person. Uses `whatsapp_business_manage_events`,
 * `page_events` or `instagram_manage_events` depending on the channel.
 */
export function ConversionEventDialog({ conversationId, platform }: { conversationId: string; platform: string }) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [eventName, setEventName] = React.useState(DEFAULT_EVENT)
  const [value, setValue] = React.useState('')
  const [currency, setCurrency] = React.useState('EUR')
  const [valueError, setValueError] = React.useState('')

  const events = useConversionEventsQuery(conversationId, open)
  const send = useSendConversionEvent(conversationId)

  const eventLabel = (name: string) => t(`conversion_events.names.${name}`, { defaultValue: name })

  if (!CONVERSION_PLATFORMS.includes(platform)) return null

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const amount = value.trim() === '' ? null : Number(value.replace(',', '.'))
    if (amount !== null && (Number.isNaN(amount) || amount < 0)) {
      setValueError(t('conversion_events.value_invalid'))
      return
    }
    setValueError('')
    send.mutate(
      { event_name: eventName, value: amount, currency: amount === null ? '' : currency },
      { onSuccess: () => setValue('') }
    )
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
            <Target className="h-4 w-4" />
            <span className="sr-only">{t('conversion_events.button')}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('conversion_events.button')}</TooltipContent>
      </Tooltip>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <form onSubmit={submit} className="space-y-4" noValidate>
            <DialogHeader>
              <DialogTitle>{t('conversion_events.title')}</DialogTitle>
              <DialogDescription>{t('conversion_events.description')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="conversion-event-name">{t('conversion_events.event')}</Label>
              <Select value={eventName} onValueChange={setEventName}>
                <SelectTrigger id="conversion-event-name">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(events.data?.event_names ?? [DEFAULT_EVENT]).map((name) => (
                    <SelectItem key={name} value={name}>
                      {eventLabel(name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="conversion-event-value">{t('conversion_events.value')}</Label>
                <Input
                  id="conversion-event-value"
                  aria-label={t('conversion_events.value')}
                  inputMode="decimal"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t('conversion_events.value_placeholder')}
                  aria-invalid={!!valueError}
                  aria-describedby={valueError ? 'conversion-event-value-error' : undefined}
                />
                <FieldError id="conversion-event-value-error">{valueError}</FieldError>
              </div>
              <div className="space-y-2">
                <Label htmlFor="conversion-event-currency">{t('conversion_events.currency')}</Label>
                <Input
                  id="conversion-event-currency"
                  aria-label={t('conversion_events.currency')}
                  value={currency}
                  maxLength={3}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
              </div>
            </div>

            {send.isError ? (
              <Alert variant="destructive">
                <AlertDescription>{conversationsErrorToUserMessage(send.error)}</AlertDescription>
              </Alert>
            ) : null}
            {send.isSuccess ? (
              <Alert>
                <AlertDescription>{t('conversion_events.sent')}</AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-2">
              <p className="text-sm font-medium">{t('conversion_events.history')}</p>
              {events.data?.events.length ? (
                <ul className="space-y-1 text-sm">
                  {events.data.events.map((event) => (
                    <li key={event.sent_at} className="flex justify-between gap-2">
                      <span>
                        {eventLabel(event.event_name)}
                        {event.value !== null ? ` · ${event.value} ${event.currency}` : ''}
                      </span>
                      <span className="text-muted-foreground">
                        {format(parseISO(event.sent_at), 'dd/MM/yyyy HH:mm')} · {event.channel}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">{t('conversion_events.empty')}</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t('conversion_events.close')}
              </Button>
              <Button type="submit" disabled={send.isPending}>
                {t('conversion_events.send')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
